/** Which way a slot cursor is being moved. */
export type SlotDirection = "left" | "right" | "up" | "down";

/**
 * Move a cursor one cell through a grid of `count` slots laid out `columns`
 * wide, and return where it lands.
 *
 * Shared by `InventoryGrid` and `Hotbar` because a hotbar is a grid one row
 * tall — or one column wide when vertical — and the arithmetic is the same
 * either way.
 *
 * Deliberately clamped rather than wrapped, for the reason
 * `FocusManager.navigate` does not wrap either: a held direction should stop
 * at the edge, not reappear on the far side. A cursor that wraps makes a grid
 * impossible to walk to the end of without overshooting.
 *
 * The last row of a partly-filled grid is respected — pressing down from a
 * cell with nothing beneath it stays put rather than landing past the end.
 */
export function stepInGrid(
  index: number,
  count: number,
  columns: number,
  direction: SlotDirection,
): number {
  if (count <= 0) return -1;
  const width = Math.max(1, Math.floor(columns));
  const from = Math.min(Math.max(Math.floor(index), 0), count - 1);
  const column = from % width;

  switch (direction) {
    case "left":
      return column > 0 ? from - 1 : from;
    case "right":
      return column < width - 1 && from + 1 < count ? from + 1 : from;
    case "up":
      return from - width >= 0 ? from - width : from;
    case "down":
      return from + width < count ? from + width : from;
    default:
      return from;
  }
}

/** The arrow key a direction corresponds to, or null for anything else. */
export function directionOfKey(key: string): SlotDirection | null {
  switch (key) {
    case "ArrowLeft":
      return "left";
    case "ArrowRight":
      return "right";
    case "ArrowUp":
      return "up";
    case "ArrowDown":
      return "down";
    default:
      return null;
  }
}
